import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import values from 'lodash/values';

import Tabs from '@bibliocommons/base-tabs';
import Heading from '@bibliocommons/heading';
import ProgressBar from '@bibliocommons/shared-progress-bar';

import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import ClearFacets from 'app/components/shared/Facets/ClearFacets';
import {
  selectActiveFilters,
  selectLocationIdToIsPreferred,
  selectOrderedLocations
} from 'app/selectors/locations/LocationsSearchSelector';
import LocationsSearchForm from './LocationsSearchForm';
import LocationItem from './LocationItem';
import LocationsActiveFilters from './LocationsActiveFilters';
import LocationsMap from './LocationsSearchForm/LocationsMap';

import './LocationsPage.scss';

const TAB_NAMES = {
  GRID: 'grid',
  MAP: 'map'
};

export default function LocationsPage() {
  const intl = useIntl();
  const loc = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(loc.search);
  const query = searchParams.get('address');

  const activeFilters = useSelector(selectActiveFilters);
  const isFetching = useSelector(state => state.getIn(['locations', 'locationsSearch', 'isFetching'], false));
  const locationIdToIsPreferred = useSelector(selectLocationIdToIsPreferred);
  let locations = useSelector(selectOrderedLocations);
  const [selectedTab, setSelectedTab] = useState(TAB_NAMES.GRID);
  const tabOptions = values(TAB_NAMES).map(name => ({
    label: intl.formatMessage({ id: `locations_tab_${name}` }),
    selected: selectedTab === name,
    value: name
  }));

  if (query) {
    const regex = new RegExp(query, 'i');
    const filteredLocations = locations.filter(l => regex.test(l.get('name')));
    if (filteredLocations.size !== 0) {
      locations = filteredLocations;
      searchParams.delete('address');
      searchParams.set('name', query);
      history.replace(`${loc.pathname}?${searchParams?.toString()}`);
    }
  }

  function renderLocations() {
    if (locations.size > 0) {
      return (
        <ul className="locations-grid">
          {locations.map(location => (
            <li key={location.get('id')}>
              <LocationItem location={location} preferred={locationIdToIsPreferred.get(location.get('id'), false)} />
            </li>
          ))}
        </ul>
      );
    }

    return (
      <div className="empty-locations">
        <Heading tagName="h2" size="medium">
          <FormattedMessage id="empty_locations_search_heading" />
        </Heading>
        <FormattedMessage id="empty_locations_search_description" tagName="p" />
        <ClearFacets>
          <FormattedMessage id="explore_all_locations" />
        </ClearFacets>
      </div>
    );
  }

  function renderLocationCount() {
    return locations.size ? (
      <div className="locations-count">
        <FormattedMessage id="locations_count" values={{ count: locations.size }} />
      </div>
    ) : null;
  }

  function renderContent() {
    switch (selectedTab) {
      case TAB_NAMES.GRID:
        return (
          <>
            {renderLocationCount()}
            {renderLocations()}
          </>
        );
      case TAB_NAMES.MAP:
        return (
          <>
            {renderLocationCount()}
            <LocationsMap />
          </>
        );
      default:
        return null;
    }
  }

  return (
    <>
      <DocumentHeadContainer pageTitle={intl.formatMessage({ id: 'locations' })} />
      <ProgressBar active={isFetching} />
      <div className="cp-locations-page">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <Heading className="page-heading" tagName="h1" size="large" variant>
                <FormattedMessage id="locations" />
              </Heading>
              <LocationsSearchForm activeFilters={activeFilters} />
              <LocationsActiveFilters activeFilters={activeFilters} />
              <Tabs options={tabOptions} renderContent={renderContent} handleSelect={setSelectedTab} centered />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
